import { makeStyles, mergeClasses } from '@griffel/react';

import type { ClassNameAndInlineStyles } from '../../util/styles/fluent9';
import { useAlignSelfClassName, useDimensions, usePadding } from '../../util/styles/fluent9';
import { px } from '../../util/styling';
import { createTokens } from '../../util/theme/fluent9';

import type { FlexItemProps } from './Flex.types';

const { tokens, cssVariables } = createTokens(['basis', 'grow', 'shrink', 'order'], 'flexItem');

type ClassNameProps = Exclude<FlexItemProps, 'children' | 'ariaHidden' | 'role'>;
type UseClassNamesAndInlineStyles = (props: ClassNameProps) => ClassNameAndInlineStyles;

export const useClassNameAndInlineStyles: UseClassNamesAndInlineStyles = ({
  height,
  minHeight,
  maxHeight,
  width,
  minWidth,
  maxWidth,

  padding,
  horizontalPadding,
  verticalPadding,
  paddingTop,
  paddingRight,
  paddingBottom,
  paddingLeft,

  alignSelf,
  basis,
  grow = 0,
  shrink = 1,
  order,
  className,
}) => {
  const classNames = useFlexItemClassNames();
  const dimensionStyling = useDimensions({
    height,
    minHeight,
    maxHeight,
    width,
    minWidth,
    maxWidth,
  });
  const paddingStyling = usePadding({
    paddingTop,
    paddingRight,
    paddingBottom,
    paddingLeft,
    padding,
    horizontalPadding,
    verticalPadding,
  });

  const hasBasis = basis != null;
  const hasOrder = order != null;

  return {
    className: mergeClasses(
      paddingStyling.className,
      dimensionStyling.className,
      useAlignSelfClassName(alignSelf),
      hasBasis && classNames.basis,
      classNames.grow,
      classNames.shrink,
      hasOrder && classNames.order,
      className
    ),
    inlineStyles: {
      ...paddingStyling.inlineStyles,
      ...dimensionStyling.inlineStyles,
      [cssVariables.basis]: hasBasis && px(basis),
      [cssVariables.grow]: grow,
      [cssVariables.shrink]: shrink,
      [cssVariables.order]: hasOrder && order,
      /**
       * This inline style is necessary to allow the minWidth prop to
       * override the higher-specificity style set by the <Flex> parent.
       */
      minWidth: px(minWidth),
    },
  };
};

const useFlexItemClassNames = makeStyles({
  basis: {
    flexBasis: tokens.basis,
  },
  grow: {
    flexGrow: tokens.grow,
  },
  shrink: {
    flexShrink: tokens.shrink,
  },
  order: {
    order: tokens.order,
  },
});
